import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Alert, Badge, Button, InlineLoader, Panel, TextField } from '../../components/ui'
import { ApiError, ContractError, NetworkError } from '../../api/client'
import {
  beginChannelLogin,
  channelQRSource,
  fetchChannelAccount,
  isSigningIn,
  signOutOfChannel,
  submitChannelPassword,
} from '../../api/channel'
import type { ChannelAccount, ChannelPhase } from '../../api/channel'
import { t } from '../../i18n'
import { useSession } from '../../auth/session'

/**
 * Connecting and disconnecting the Telegram account.
 *
 * Three things here are decisions rather than styling:
 *
 *   - The screen polls while a sign-in is running and stops the moment it is
 *     not. A code lives about half a minute and is replaced; a screen showing
 *     the previous one reads as "scanning does nothing", which is the failure
 *     somebody would spend ten minutes on before doubting the picture.
 *   - Signing out asks first, and says in the confirmation what it does and
 *     does not do. The fear it has to answer is "will I lose the
 *     conversations", and the answer is no: the history stays, the session file
 *     goes.
 *   - The password field appears only when Telegram has asked for it. A field
 *     on screen the whole time invites somebody to type a password into a
 *     server that has no use for it yet.
 */
export function TelegramAccount() {
  const { can } = useSession()
  const queryClient = useQueryClient()

  const [password, setPassword] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [confirmingSignOut, setConfirmingSignOut] = useState(false)

  const account = useQuery({
    queryKey: CHANNEL_ACCOUNT_KEY,
    queryFn: ({ signal }) => fetchChannelAccount(signal),
    // Only while something is happening. A screen that polls a connected
    // account every two seconds forever is a screen that keeps a laptop awake
    // for no news.
    refetchInterval: (query) => {
      const phase = query.state.data?.phase
      return phase !== undefined && isSigningIn(phase) ? POLL_MS : false
    },
  })

  function afterChange(next: ChannelAccount) {
    setFailure(null)
    queryClient.setQueryData(CHANNEL_ACCOUNT_KEY, next)
  }

  const begin = useMutation({
    mutationFn: beginChannelLogin,
    onSuccess: afterChange,
    onError: (error) => setFailure(messageFor(error)),
  })

  const send = useMutation({
    mutationFn: submitChannelPassword,
    onSuccess: (next) => {
      // Cleared the moment it has been handed over, so it is not sitting in a
      // component while somebody walks away from the desk.
      setPassword('')
      afterChange(next)
    },
    onError: (error) => {
      setPassword('')
      setFailure(messageFor(error))
    },
  })

  const signOut = useMutation({
    mutationFn: signOutOfChannel,
    onSuccess: (next) => {
      setConfirmingSignOut(false)
      afterChange(next)
    },
    onError: (error) => {
      setConfirmingSignOut(false)
      setFailure(messageFor(error))
    },
  })

  if (account.isLoading) {
    return (
      <Panel>
        <InlineLoader />
      </Panel>
    )
  }
  if (account.data === undefined) {
    return (
      <Panel>
        <Alert>{t('channel.unreadable')}</Alert>
      </Panel>
    )
  }

  return (
    <TelegramAccountView
      account={account.data}
      mayConnect={can('settings.write')}
      busy={begin.isPending || send.isPending || signOut.isPending}
      failure={failure}
      password={password}
      confirmingSignOut={confirmingSignOut}
      onPassword={setPassword}
      onBegin={() => {
        setFailure(null)
        begin.mutate()
      }}
      onSubmitPassword={() => send.mutate(password)}
      onAskSignOut={() => {
        setFailure(null)
        setConfirmingSignOut(true)
      }}
      onCancelSignOut={() => setConfirmingSignOut(false)}
      onSignOut={() => signOut.mutate()}
    />
  )
}

const CHANNEL_ACCOUNT_KEY = ['channel-account'] as const

/** Often enough that a scanned code turns into a name while somebody watches. */
const POLL_MS = 2000

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof NetworkError) return t('errors.network')
  if (error instanceof ContractError) return t('errors.badResponse')
  return t('channel.failed')
}

interface ViewProps {
  readonly account: ChannelAccount
  readonly mayConnect: boolean
  readonly busy: boolean
  readonly failure: string | null
  readonly password: string
  readonly confirmingSignOut: boolean
  readonly onPassword: (value: string) => void
  readonly onBegin: () => void
  readonly onSubmitPassword: () => void
  readonly onAskSignOut: () => void
  readonly onCancelSignOut: () => void
  readonly onSignOut: () => void
}

/**
 * What the panel shows, given only the account's state.
 *
 * Kept apart from the mutations so that what appears is a function of what the
 * server says and of how the last attempt went, never of what somebody just
 * pressed. A refusal therefore leaves the screen showing the state the account
 * is actually in.
 */
export function TelegramAccountView({
  account,
  mayConnect,
  busy,
  failure,
  password,
  confirmingSignOut,
  onPassword,
  onBegin,
  onSubmitPassword,
  onAskSignOut,
  onCancelSignOut,
  onSignOut,
}: ViewProps) {
  const { phase } = account

  return (
    <Panel className="channel">
      <header className="channel__header">
        <h2 className="channel__title">{t('channel.title')}</h2>
        <Badge tone={toneFor(phase)}>{t(phaseLabel(phase))}</Badge>
      </header>

      {phase === 'unavailable' ? <p className="channel__note">{t('channel.elsewhere')}</p> : null}

      {/*
        Two different problems, and the screen used to give one answer to both:
        it listed api_id, api_hash and the session key whenever the channel was
        switched off — having checked none of them, because the configuration is
        only validated when the channel is on.
      */}
      {phase === 'unavailable' || (account.configured && account.enabled) ? null : !account.enabled ? (
        <p className="channel__note">{t('channel.switchedOff')}</p>
      ) : account.missing.length > 0 ? (
        <p className="channel__note">
          {t('channel.missing', { names: account.missing.join(', ') })}
        </p>
      ) : (
        // Configured is false and nothing is named, which the server does not
        // produce. Saying the general thing beats naming an empty list.
        <p className="channel__note">{t('channel.notConfigured')}</p>
      )}

      {failure === null ? null : <Alert>{failure}</Alert>}
      {account.failure === '' ? null : <Alert tone="warning">{account.failure}</Alert>}

      {phase === 'connected' ? (
        <Signed account={account} /> // who we are attached to, in words
      ) : null}

      {phase === 'waiting_for_scan' && account.qrUrl !== undefined ? (
        <div className="channel__qr">
          <ol className="channel__steps">
            <li>{t('channel.step1')}</li>
            <li>{t('channel.step2')}</li>
            <li>{t('channel.step3')}</li>
          </ol>
          {/*
            Keyed by the token so React replaces the element when Telegram
            reissues one. Without the key the browser keeps the picture it
            already has, and the code on screen quietly stops working.
          */}
          <img
            key={account.qrUrl}
            className="channel__qr-image"
            src={channelQRSource(account.qrUrl)}
            alt={t('channel.qrAlt')}
            width={256}
            height={256}
          />
        </div>
      ) : null}

      {phase === 'waiting_for_password' ? (
        <form
          className="channel__password"
          onSubmit={(event) => {
            event.preventDefault()
            if (password !== '') onSubmitPassword()
          }}
        >
          <p className="channel__note">{t('channel.passwordWhy')}</p>
          <TextField
            label={t('channel.passwordLabel')}
            type="password"
            autoComplete="off"
            value={password}
            onChange={(event) => onPassword(event.target.value)}
          />
          <Button type="submit" busy={busy} disabled={password === ''}>
            {t('channel.passwordSubmit')}
          </Button>
        </form>
      ) : null}

      {phase === 'starting' || phase === 'connecting' ? (
        <InlineLoader label={t(phaseLabel(phase))} />
      ) : null}

      {/*
        Controls last, and only for somebody who may move the account. A
        disabled button would tell a reader they are one click from connecting
        the agency's Telegram, which they are not.
      */}
      {mayConnect && account.configured && phase !== 'unavailable' ? (
        <footer className="channel__actions">
          {phase === 'signed_out' || phase === 'failed' ? (
            <Button busy={busy} onClick={onBegin}>
              {t(phase === 'failed' ? 'channel.retry' : 'channel.signIn')}
            </Button>
          ) : null}

          {phase === 'connected' && !confirmingSignOut ? (
            <Button variant="danger" onClick={onAskSignOut}>
              {t('channel.signOut')}
            </Button>
          ) : null}

          {isSigningIn(phase) ? (
            <Button variant="ghost" busy={busy} onClick={onSignOut}>
              {t('channel.cancel')}
            </Button>
          ) : null}

          {confirmingSignOut ? (
            <div className="confirm">
              <p className="confirm__title">{t('channel.signOutConfirmTitle')}</p>
              <p className="confirm__hint">{t('channel.signOutConfirmHint')}</p>
              <div className="confirm__actions">
                <Button variant="ghost" onClick={onCancelSignOut}>
                  {t('common.cancel')}
                </Button>
                <Button variant="danger" busy={busy} onClick={onSignOut}>
                  {t('channel.signOutConfirm')}
                </Button>
              </div>
            </div>
          ) : null}
        </footer>
      ) : null}
    </Panel>
  )
}

function Signed({ account }: { readonly account: ChannelAccount }) {
  const name = account.name === '' ? t('channel.unnamed') : account.name

  return (
    <dl className="channel__identity">
      <div>
        <dt>{t('channel.who')}</dt>
        <dd>{account.username === '' ? name : `${name} (@${account.username})`}</dd>
      </div>
      {account.phoneMasked === '' ? null : (
        <div>
          <dt>{t('channel.phone')}</dt>
          <dd>{account.phoneMasked}</dd>
        </div>
      )}
    </dl>
  )
}

function phaseLabel(phase: ChannelPhase) {
  switch (phase) {
    case 'connected':
      return 'channel.phaseConnected' as const
    case 'signed_out':
      return 'channel.phaseSignedOut' as const
    case 'starting':
      return 'channel.phaseStarting' as const
    case 'waiting_for_scan':
      return 'channel.phaseWaitingScan' as const
    case 'waiting_for_password':
      return 'channel.phaseWaitingPassword' as const
    case 'connecting':
      return 'channel.phaseConnecting' as const
    case 'failed':
      return 'channel.phaseFailed' as const
    default:
      return 'channel.phaseUnavailable' as const
  }
}

function toneFor(phase: ChannelPhase) {
  if (phase === 'connected') return 'accent' as const
  if (phase === 'failed') return 'danger' as const
  if (phase === 'signed_out' || phase === 'unavailable') return 'muted' as const
  return 'warning' as const
}
